import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, Box, Grid, Modal, Tooltip, IconButton, Card, CardContent, TextField, Chip } from '@mui/material';
import { getErrorMessage, timeGreetings } from "../utils/helper.js";
import { useLocation, useNavigate } from "react-router-dom";
import axios from 'axios';
import { danger, success, warning } from '../utils/message-box.js';
import CustomizedSnackbars from '../utils/snackbar.js';
import InfoIcon from "@mui/icons-material/Info";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ListIcon from "@mui/icons-material/List";
import InventoryIcon from "@mui/icons-material/Inventory";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import illustration from '../assets/images/illustration.png';
import axiosInstance from '../Services/axiosInstance.js';
import Benfit from './benfitScreen.js';
import FallingSprinkles from '../components/FallingSprinkles.js';
import { getSpecialDay } from '../utils/specialDays.js';

const formatDuration = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hours}h ${mins.toString().padStart(2, '0')}m`;
};

const getLiveWorkedMinutes = (attendanceToday) => {
  if (!attendanceToday) return 0;
  if (attendanceToday.clock_in_at) {
    const completed = attendanceToday.completed_worked_minutes ?? 0;
    const sessionMinutes = Math.max(
      0,
      Math.floor((Date.now() - new Date(attendanceToday.clock_in_at).getTime()) / 60000)
    );
    return completed + sessionMinutes;
  }
  return attendanceToday.worked_minutes ?? attendanceToday.completed_worked_minutes ?? 0;
};

const formatAttendanceStatus = (status) => {
  if (!status) return 'Unknown';
  if (status === 'short_day') return 'Short day';
  if (status === 'half_day') return 'Half day';
  if (status === 'unplanned_leave') return 'Unplanned leave';
  if (status === 'planned_leave') return 'Planned leave';
  if (status === 'festival') return 'Festival';
  if (status === 'regularization_pending') return 'Regularization pending';
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
};

const WelcomeScreen = () => {
  const navigate = useNavigate();
  const [specialDay, setSpecialDay] = useState(null);
  const [uploadModal, setUploadModal] = useState(false);
  const [filename, setFilename] = useState("");
  const [extension, setExtension] = useState("");
  const [pictureFile, setPictureFile] = useState(null);
  const [isFileValid, setIsFileValid] = useState(false);
  const [openSnackbar, setOpenSnakbar] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [unMatchedData, setUnMatchedData] = useState([]);
  const [existingData, setExistingData] = useState([]);
  const [infoModel, setInfoModel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSkip, setIsSkip] = useState();
  const [status, setStatus] = useState("");
  const [stats, setStats] = useState({
    totalCustomers: 0,
    pendingOrders: 0,
    deliveredOrders: 0,
    returnedOrders: 0,
    returnedOrdersByBusinessType: {}
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [attendanceToday, setAttendanceToday] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(true);
  const [regularizeModal, setRegularizeModal] = useState(false);
  const [regularizeFields, setRegularizeFields] = useState({
    attendance_date: '',
    reason: '',
    proposed_clock_in_at: '',
    proposed_clock_out_at: '',
  });
  const [regularizeErrors, setRegularizeErrors] = useState({});
  const [workProgress, setWorkProgress] = useState(null);

  useEffect(() => {
    try {
      setSpecialDay(getSpecialDay(new Date()));
    } catch (error) {
      console.error('Error fetching special days:', error);
    }
  }, []);

  const handleConfirmPopup = () => {
    setUploadModal(false);
    setPictureFile(null);
    setFilename("");
  };
  function ValidateSingleInput(event) {
    var validFileExtensions = ["xlsx"];
    var file = event.target.files[0];
    if (file) {
      var extension = file.name.split(".").pop();
      if (validFileExtensions.includes(extension)) {
        setFilename(file.name);
        setExtension(extension);
        setPictureFile(file);
        setIsFileValid(true);
      } else {
        setIsFileValid(false);
        setFilename("");
        setExtension("");
        danger('Please upload only xlsx format!');
      }
      event.target.value = null;
    }
    return true;
  };
  useEffect(() => {
    fetchStats();
    fetchAttendanceToday();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!attendanceToday) {
        setWorkProgress(null);
        return;
      }
      const liveMinutes = getLiveWorkedMinutes(attendanceToday);
      if (liveMinutes > 0 || attendanceToday.clock_in_at) {
        setWorkProgress({ elapsedDisplay: formatDuration(liveMinutes) });
      } else {
        setWorkProgress(null);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [attendanceToday]);

  useEffect(() => {
    if (!attendanceToday) {
      setWorkProgress(null);
      return;
    }
    const liveMinutes = getLiveWorkedMinutes(attendanceToday);
    if (liveMinutes > 0 || attendanceToday.clock_in_at) {
      setWorkProgress({ elapsedDisplay: formatDuration(liveMinutes) });
    } else {
      setWorkProgress(null);
    }
  }, [attendanceToday]);

  const fetchAttendanceToday = async () => {
    try {
      setLoadingAttendance(true);
      const response = await axiosInstance.get('/api/v1/attendance/today');
      setAttendanceToday(response.data?.data || null);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setAttendanceToday(null);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const handleClockIn = async () => {
    try {
      await axiosInstance.post('/api/v1/attendance/clock-in');
      success('Clocked in successfully.');
      fetchAttendanceToday();
    } catch (error) {
      danger(getErrorMessage(error, 'Failed to clock in.'));
    }
  };

  const handleClockOut = async () => {
    try {
      await axiosInstance.post('/api/v1/attendance/clock-out');
      success('Clocked out successfully.');
      fetchAttendanceToday();
    } catch (error) {
      danger(getErrorMessage(error, 'Failed to clock out.'));
    }
  };

  const openRegularizeModal = () => {
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    setRegularizeFields({
      attendance_date: today,
      reason: '',
      proposed_clock_in_at: `${today}T09:30`,
      proposed_clock_out_at: `${today}T17:30`,
    });
    setRegularizeErrors({});
    setRegularizeModal(true);
  };

  const validateRegularization = () => {
    const errors = {};
    if (!regularizeFields.attendance_date) {
      errors.attendance_date = 'Date is required';
    }
    if (!regularizeFields.reason?.trim()) {
      errors.reason = 'Reason is required';
    }
    if (!regularizeFields.proposed_clock_in_at) {
      errors.proposed_clock_in_at = 'Proposed clock in is required';
    }
    if (!regularizeFields.proposed_clock_out_at) {
      errors.proposed_clock_out_at = 'Proposed clock out is required';
    } else if (
      regularizeFields.proposed_clock_in_at &&
      new Date(regularizeFields.proposed_clock_out_at) <= new Date(regularizeFields.proposed_clock_in_at)
    ) {
      errors.proposed_clock_out_at = 'Clock out must be after clock in';
    }
    setRegularizeErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitRegularization = async () => {
    if (!validateRegularization()) {
      danger('Please fill all required fields.');
      return;
    }
    try {
      await axiosInstance.post('/api/v1/attendance/regularize', {
        attendance_date: regularizeFields.attendance_date,
        reason: regularizeFields.reason.trim(),
        proposed_clock_in_at: new Date(regularizeFields.proposed_clock_in_at).toISOString(),
        proposed_clock_out_at: new Date(regularizeFields.proposed_clock_out_at).toISOString(),
      });
      success('Regularization request submitted for admin approval.');
      setRegularizeModal(false);
      fetchAttendanceToday();
    } catch (error) {
      danger(getErrorMessage(error, 'Failed to submit regularization request.'));
    }
  };

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const response = await axiosInstance.get('/api/v1/customers/stats');
      const statsData = response.data?.data || {};
      
      setStats({
        totalCustomers: statsData.totalCustomers || 0,
        pendingOrders: statsData.pendingOrders || 0,
        deliveredOrders: statsData.deliveredOrders || 0,
        returnedOrders: statsData.returnedOrders || 0,
        returnedOrdersByBusinessType: statsData.returnedOrdersByBusinessType || {}
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set default values on error
      setStats({
        totalCustomers: 0,
        pendingOrders: 0,
        deliveredOrders: 0,
        returnedOrders: 0,
        returnedOrdersByBusinessType: {}
      });
    } finally {
      setLoadingStats(false);
    }
  };

  const Submit = (value) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("file", pictureFile)
    let url = "";
    if (value === undefined) {
      url = "api/v1/customers/upload"
    } else {
      url = "api/v1/customers/upload?isSkip=" + value
    }
    axiosInstance.post(url, formData)
      .then((res) => {
        const { status, successCount, totalRows, unmatched_barcodes, validation_errors, warnings, existing_barcodes } = res.data;
        
        // Show unmatched barcodes
        if (status === "CONFIRMATION_REQUIRED") {
          setInfoModel(true);
          setStatus(status);
          setUnMatchedData(unmatched_barcodes);
          setExistingData(existing_barcodes);
        } else if (unmatched_barcodes?.length > 0) {
          setInfoModel(true);
          setStatus(status);
          setUnMatchedData(unmatched_barcodes);
        }
        
        // Show validation errors
        if (validation_errors?.length > 0) {
          const errorMsg = validation_errors.map(e => 
            `Row ${e.row} (${e.barcode}): ${e.error}`
          ).join('\n');
          danger(`Upload completed with errors:\n${errorMsg}`);
        }
        
        // Show warnings
        if (warnings?.length > 0) {
          const warningMsg = warnings.map(w => 
            `Row ${w.row} (${w.barcode}): ${w.warning}`
          ).join('\n');
          warning(warningMsg);
        }
        
        // Show success message
        setTimeout(() => {
          if (status === 'Success') {
            success(`Document uploaded successfully! ${successCount}/${totalRows} rows processed.`);
            setPictureFile(null);
            setFilename("");
          } else if (status === 'Partial Success') {
            warning(`Partial success: ${successCount}/${totalRows} rows processed. Check errors above.`);
            setPictureFile(null);
            setFilename("");
          }
        }, warnings?.length > 0 ? 10000 : 0);
        
        // setPictureFile(null);
        // setFilename("");
        setUploadModal(false);
        setIsLoading(false);
        // Refresh stats after upload
        fetchStats();
      }).catch((e) => {
        danger(getErrorMessage(e, 'Failed to upload file. Please try again.'));
        setIsLoading(false);
      });
  };
  const username = localStorage.getItem('username')?.split('@')[0] || 'User';

  return (
    <>
      {specialDay && <FallingSprinkles />}
      <Grid className="welcome">
        {/* Welcome Banner */}
        <Card 
          sx={{
            marginBottom: '24px',
            // padding: '32px',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '24px',
            flexWrap: { xs: 'wrap', md: 'nowrap' }
          }}>
            <Box sx={{ flex: 1, padding: '32px' }}>
              <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: '8px' }}>
                👋 Hello, {username}!
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '16px' }}>
                Welcome back! Manage your orders, customers, and inventory from here.
              </Typography>
            </Box>
            <Grid sx={{ 
              display: { xs: 'none', md: 'block' },
              minWidth: '200px',
              maxWidth: '415px',
              flexShrink: 0
            }}>
              <img src={illustration} alt="illustration" style={{ width: '100%', height: 'auto', objectFit: 'contain' }} />
            </Grid>
          </Box>
        </Card>

        {!loadingAttendance && attendanceToday?.is_registered_employee && (
          <Card sx={{ marginBottom: '24px', boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)', display: { xs: 'none', md: 'block' } }}>
            <CardContent sx={{ padding: '24px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '8px' }}>
                    Today&apos;s Attendance
                  </Typography>
                  {attendanceToday?.status === 'planned_leave' && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                      {attendanceToday?.message || 'Planned leave for today'}
                    </Typography>
                  )}
                  {workProgress && attendanceToday?.status !== 'planned_leave' && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Worked today: {workProgress.elapsedDisplay}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={attendanceToday?.status === 'not_clocked_in' ? 'Not clocked in' : formatAttendanceStatus(attendanceToday?.status)}
                      color={
                        attendanceToday?.status === 'present' || attendanceToday?.status === 'regularized'
                          ? 'success'
                          : attendanceToday?.status === 'planned_leave'
                            ? 'info'
                          : attendanceToday?.status === 'short_day' || attendanceToday?.status === 'half_day'
                            ? 'warning'
                            : 'default'
                      }
                    />
                    {attendanceToday?.late_display ? (
                      <Chip size="small" color="warning" label={attendanceToday.late_display} />
                    ) : null}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {attendanceToday?.can_clock_in && (
                    <Button className="btn profile-button"  sx={{ backgroundColor: 'red' }} onClick={handleClockIn}>Clock In</Button>
                  )}
                  {attendanceToday?.can_clock_out && (
                    <Button className="btn profile-button" sx={{ backgroundColor: 'red' }} onClick={handleClockOut}>
                      Clock Out
                    </Button>
                  )}
                  <Button className="btn primary-button" onClick={() => navigate('/attendance', { state: { openPlannedLeave: true } })}>Request Leave</Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Action Cards */}
        <Grid container spacing={3} sx={{ marginBottom: '32px' }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)'
                }
              }}
              onClick={() => navigate('/createCustomer')}
            >
              <CardContent sx={{ padding: '24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Box sx={{ 
                    width: '56px', 
                    height: '56px', 
                    borderRadius: '15%', 
                    backgroundColor: '#E8F5E9', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <PersonAddIcon sx={{ width: '2em', height: '2em',fontSize: '28px', color: '#388E3C' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '4px' }}>
                      Add Customer
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Create new customer
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)'
                }
              }}
              onClick={() => setUploadModal(true)}
            >
              <CardContent sx={{ padding: '24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Box sx={{ 
                    width: '56px', 
                    height: '56px', 
                    borderRadius: '15%', 
                    backgroundColor: '#E3F2FD', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <UploadFileIcon sx={{ width: '2em', height: '2em', fontSize: '28px', color: '#1976D2' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '4px' }}>
                      Upload Orders
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Upload bulk orders
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)'
                }
              }}
              onClick={() => navigate('/customerList')}
            >
              <CardContent sx={{ padding: '24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Box sx={{ 
                    width: '56px', 
                    height: '56px', 
                    borderRadius: '15%', 
                    backgroundColor: '#E8F5E9', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <ListIcon sx={{ width: '2em', height: '2em', fontSize: '28px', color: '#388E3C' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '4px' }}>
                      View Customers
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Browse all customers
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)'
                }
              }}
              onClick={() => navigate('/summary')}
            >
              <CardContent sx={{ padding: '24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Box sx={{ 
                    width: '56px', 
                    height: '56px', 
                    borderRadius: '15%', 
                    backgroundColor: '#F3E5F5', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <InventoryIcon sx={{ width: '2em', height: '2em',fontSize: '28px', color: '#7B1FA2' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '4px' }}>
                      Inventory
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Check stock levels
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        

        {/* Quick Stats Section */}
        <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '16px' }}>
          Quick Stats
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)' }}>
              <CardContent sx={{ padding: '24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Box sx={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '15%', 
                    backgroundColor: '#E8F5E9', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <PersonAddIcon sx={{ width: '2em', height: '2em',fontSize: '24px', color: '#388E3C' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, marginBottom: '4px' }}>
                      {loadingStats ? '...' : stats.totalCustomers.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Total Orders
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)' }}>
              <CardContent sx={{ padding: '24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Box sx={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '15%', 
                    backgroundColor: '#FFF3E0', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <AccessTimeIcon sx={{ width: '2em', height: '2em', fontSize: '24px', color: '#F57C00' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, marginBottom: '4px' }}>
                      {loadingStats ? '...' : stats.pendingOrders}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Pending Orders
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)' }}>
              <CardContent sx={{ padding: '24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Box sx={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '15%', 
                    backgroundColor: '#E8F5E9', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <CheckCircleIcon sx={{ width: '2em', height: '2em', fontSize: '24px', color: '#388E3C' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, marginBottom: '4px' }}>
                      {loadingStats ? '...' : stats.deliveredOrders}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Delivered & Dispatched Orders
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)' }}>
              <CardContent sx={{ padding: '24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <Box sx={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '15%', 
                    backgroundColor: '#FFEBEE', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <RefreshIcon sx={{ width: '2em', height: '2em', fontSize: '24px', color: '#C62828' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, marginBottom: '4px' }}>
                      {loadingStats ? '...' : stats.returnedOrders}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Returned Orders
                      </Typography>
                      <Tooltip
                        arrow
                        title={
                          <Box>
                            {Object.entries(stats.returnedOrdersByBusinessType)
                              .sort(([firstType], [secondType]) => firstType.localeCompare(secondType))
                              .map(([businessType, count]) => (
                                <Typography key={businessType} variant="body2">
                                  {businessType}: {count}
                                </Typography>
                              ))}
                            {!loadingStats && Object.keys(stats.returnedOrdersByBusinessType).length === 0 && (
                              <Typography variant="body2">No returned orders</Typography>
                            )}
                          </Box>
                        }
                      >
                        <IconButton
                          size="small"
                          aria-label="Returned orders by business type"
                          sx={{ padding: '2px', marginLeft: '4px' }}
                        >
                          <InfoIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
      <Modal
                open={infoModel}
                // onClose={() => setInfoModel(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box className="modal-content inputcontrasts">
                    <Typography className="align-left">
                        <Typography
                            id="modal-modal-title"
                            variant="h5"
                            className="font-weight-700"
                        >
                            {status === "CONFIRMATION_REQUIRED" ? "Confirmation ⚠️" : "Information"}
                        </Typography>
                        <Typography sx={{ mt: 2 }} className="sub-text-font">
                           {existingData?.length ? "Found barcodes that already exist. Would you like to update them, or would you prefer to skip?" : status === "Success" ? "Created Successfully" : "The barcodes listed below do not match any existing data, hence they will be created as new records."}
                        </Typography>
                        <Typography className="sub-text-font">Total Count: {existingData?.length ? existingData?.length + unMatchedData?.length : unMatchedData?.length}</Typography>
                        <Typography sx={{ mt: 1 }} className="sub-text-font font-weight-600 word-break upload-model">
                            {existingData?.length ? <span><u>Existing order Id's:</u> {existingData?.map((id) => `${id},`)}</span> : ""}<br />
                            <span><u>New order Id's:</u> {unMatchedData?.map((id) => `${id},`)}</span>
                        </Typography>
                        <Typography className="text-end mt-20">
                        {status !== "CONFIRMATION_REQUIRED" ? <Button
                                className="btn primary-button"
                                onClick={() => {
                                  setInfoModel(false);
                                  setUnMatchedData([]);
                                }}
                                aria-label="verify"
                                disableRipple="true"
                            >
                                OK
                            </Button> :
                            <div className="d-flex justify-end">
                              <Button
                                className="btn profile-button"
                                sx={{ backgroundColor: 'red' }}
                                onClick={() => {
                                  setInfoModel(false);
                                  setUnMatchedData([]);
                                  setExistingData([]);
                                  Submit(false);
                                }}
                                aria-label="verify"
                                disableRipple="true"
                            >
                                Continue
                            </Button>
                            {existingData?.length ? <Button
                                className="btn primary-button ml-5"
                                onClick={() => {
                                  setInfoModel(false);
                                  setUnMatchedData([]);
                                  setExistingData([]);
                                  Submit(true);
                                }}
                                aria-label="verify"
                                disableRipple="true"
                            >
                                Skip Update
                            </Button> : ""}
                            </div>}
                        </Typography>
                    </Typography>
                </Box>
            </Modal>
      <Modal
        open={uploadModal}
        onClose={handleConfirmPopup}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="modal-content inputcontrasts">
          <Typography className="align-left">
            <Typography
              id="modal-modal-title"
              variant="h5"
              className="font-weight-700"
            >
              Upload File
            </Typography>
            <Box className="text-center">
              <Typography sx={{ mt: 2 }} className="sub-text-font">
                <b>Note</b>
              </Typography>
              <Typography sx={{ mt: 2 }} className="sub-text-font display-flex pl-40 pr-40 mt-0">
                <p className="">To generate or update customer data, upload a file with relevant field names.</p>
                <Tooltip
                  placement="top"
                  title="name, address, phone_number, bighat_order_id, quantity (particular size quantity), complaint_number, prepaid, pincode, barcode, remarks, business_type, weight, delivary_charges, cod, 
                             order_status, received_cod_amount, created_at, order_quantity (total order quantity), Size Details, commision, payment_status."
                  enterTouchDelay={0}
                >
                  <IconButton
                    style={{
                      verticalAlign: "middle",
                      padding: "0px",
                      marginBottom: "2px",
                      marginLeft: "2px",
                    }}
                    aria-label="Info icon"
                  >
                    <InfoIcon aria-label="Info icon" />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography className="sub-text-font mb-5 font-weight-600">{filename}</Typography>
              <input id='file-upload' accept='.xlsx' type='file' hidden onChange={ValidateSingleInput} />
              <Button
                className="btn primary-button"
                aria-label="button"
                onClick={() => document.getElementById("file-upload").click()}
              >
                Upload file
              </Button>
            </Box>
            <Typography className="text-end mt-20">
              <Button
                className="btn secondary-button m-r-10"
                aria-label="button"
                onClick={() => handleConfirmPopup()}
              >
                Cancel
              </Button>
              <Button
                className="btn primary-button"
                onClick={() => Submit()}
                disabled={!pictureFile}
                aria-label="verify"
                disableRipple="true"
              >
              {isLoading ? <span className="loader-login"></span> : "OK"}
              </Button>
            </Typography>
          </Typography>
        </Box>
      </Modal>

      <Modal open={regularizeModal} onClose={() => setRegularizeModal(false)}>
        <Box className="modal-content inputcontrasts">
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Request Regularization</Typography>
          <Typography className="gray8 font-weight-500 f-14">Date<span className="gray9">*</span></Typography>
          <TextField
            fullWidth
            margin="normal"
            type="date"
            className={`form-control input-group mb-0 inputcontrasts modal-border ${regularizeErrors.attendance_date ? 'is-invalid' : ''}`}
            value={regularizeFields.attendance_date}
            onChange={(e) => setRegularizeFields({ ...regularizeFields, attendance_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            error={!!regularizeErrors.attendance_date}
          />
          <span className="danger-color error-msg">{regularizeErrors.attendance_date || ''}</span>

          <Typography className="gray8 font-weight-500 f-14 mt-10">Reason<span className="gray9">*</span></Typography>
          <textarea
            rows={3}
            placeholder="Enter reason for regularization"
            className={`form-control input-group inputcontrasts modal-border w-100 ${regularizeErrors.reason ? 'is-invalid' : ''}`}
            value={regularizeFields.reason}
            onChange={(e) => setRegularizeFields({ ...regularizeFields, reason: e.target.value })}
          />
          <span className="danger-color error-msg">{regularizeErrors.reason || ''}</span>

          <Typography className="gray8 font-weight-500 f-14 mt-10">Proposed Clock In<span className="gray9">*</span></Typography>
          <TextField
            fullWidth
            margin="normal"
            type="datetime-local"
            className={`form-control input-group mb-0 inputcontrasts modal-border ${regularizeErrors.proposed_clock_in_at ? 'is-invalid' : ''}`}
            value={regularizeFields.proposed_clock_in_at}
            onChange={(e) => setRegularizeFields({ ...regularizeFields, proposed_clock_in_at: e.target.value })}
            InputLabelProps={{ shrink: true }}
            error={!!regularizeErrors.proposed_clock_in_at}
          />
          <span className="danger-color error-msg">{regularizeErrors.proposed_clock_in_at || ''}</span>

          <Typography className="gray8 font-weight-500 f-14 mt-10">Proposed Clock Out<span className="gray9">*</span></Typography>
          <TextField
            fullWidth
            margin="normal"
            type="datetime-local"
            className={`form-control input-group mb-0 inputcontrasts modal-border ${regularizeErrors.proposed_clock_out_at ? 'is-invalid' : ''}`}
            value={regularizeFields.proposed_clock_out_at}
            onChange={(e) => setRegularizeFields({ ...regularizeFields, proposed_clock_out_at: e.target.value })}
            InputLabelProps={{ shrink: true }}
            error={!!regularizeErrors.proposed_clock_out_at}
          />
          <span className="danger-color error-msg">{regularizeErrors.proposed_clock_out_at || ''}</span>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button className="btn secondary-button" onClick={() => setRegularizeModal(false)}>Cancel</Button>
            <Button className="btn primary-button" onClick={submitRegularization}>Submit</Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default WelcomeScreen;
